/**
 * Which `?view=` belongs to which page — the routing half of the 2026-09-12
 * split of «عملیات و هزینه» into «مدیریت هزینه‌ها» and «کالاها و خدمات»
 * (docs/CLINIC_EXPENSES_AND_GOODS_SPLIT_DESIGN.md).
 *
 * **Why this is its own module.** Three EAGER consumers need this fact before
 * any screen chunk loads: the router's moved-view guard (`App.jsx`), the
 * pre-chunk skeleton registry (`screens/skeletons.jsx`), and the rail
 * definitions themselves (`screens/Operations/Operations.shared.js`). The rails
 * are the natural place to DECLARE a destination, but that module is ~1400
 * lines of vocabulary, money helpers, Jalali arithmetic and the equipment state
 * machine — pulling it into the entry bundle to answer "which page owns
 * `items`?" would make every first paint pay for the answer.
 *
 * So the two key lists live here, with no imports at all, and the rails are
 * checked against them. One source, and the check is a test rather than a
 * review: `Operations.shared.test.js` asserts each rail's keys are exactly the
 * list below, in order — a destination added to a rail and forgotten here (or
 * the reverse) fails immediately instead of producing a view that renders but
 * cannot be linked to.
 *
 * Nothing here is user-facing. A URL has no language, so there is no `t`.
 */

/** «مدیریت هزینه‌ها» — money out, and the definitions that classify it. */
export const EXPENSES_PATH = '/operations';

/** «کالاها و خدمات» — what the clinic stocks, offers and owns. */
export const GOODS_PATH = '/goods';

/**
 * The expense page's views, in rail order.
 *
 * The page kept the `/operations` path through the split: the bank's Sepehr
 * return URL (`parsina.bff.clinic/src/payments/sepehrBillCallback.js`) and the
 * reports resolver's attention links are built server-side and name it, so
 * renaming the route would have required a BFF deploy ordered against the
 * client's — and a redirect that then had to exist forever.
 */
export const EXPENSES_VIEWS = [
  'expenses', 'bills',
  'recurring', 'billTypes',
];

/**
 * The goods page's views, in rail order.
 *
 * Eleven of the merged module's sixteen destinations. `equipment` and
 * `insurance` are the two whose home was a genuine decision rather than an
 * obvious one — see split design §12 D1 and D2.
 */
export const GOODS_VIEWS = [
  'services', 'items', 'equipment',
  'suppliers', 'equipmentTypes',
  'expiry', 'procurement', 'custody', 'counts', 'locations',
  'insurance',
];


/**
 * The accounting hub's views, in rail order
 * (docs/CLINIC_ACCOUNTING_FINOPS_DESIGN.md §4.2).
 *
 * Same contract as the two lists above and for the same reason: the router, the
 * pre-chunk skeleton registry and the rail all need this fact before the screen's
 * chunk loads, and `Accounting.shared.js` is checked against it by a test rather
 * than by review — a destination added to the rail and forgotten here (or the
 * reverse) fails immediately instead of producing a view that renders but cannot
 * be linked to.
 */
export const ACCOUNTING_VIEWS = [
  'dashboard', 'transactions', 'receipts', 'payments', 'payroll',
  'accounts', 'counterparties', 'receivables', 'payables',
];

const ACCOUNTING_SET = new Set(ACCOUNTING_VIEWS);

/**
 * Does this `?view=` belong to the accounting hub?
 *
 * Deliberately NOT folded into `pageForView`: three of these keys
 * (`expenses`, `services`, `items`…) are words the other two pages also use, and a
 * single map keyed on the bare view name would have to pick a winner. The hub's
 * membership is asked as its own question, by the one caller that has already
 * decided it is looking at `/accounting`.
 */
export const isAccountingView = (view) => !!view && ACCOUNTING_SET.has(view);


/**
 * The store's views, in rail order
 * (docs/CLINIC_STORE_MARKETPLACE_DESIGN.md §8.2).
 *
 * Same contract as the accounting hub: the router, the pre-chunk skeleton
 * registry and the rail all need this fact before the screen's chunk loads, and
 * `Store.shared.js` is checked against it by a test rather than by review.
 */
export const STORE_VIEWS = [
  'dashboard', 'orders', 'listings',
  'storefront', 'finance', 'settings',
];

const STORE_SET = new Set(STORE_VIEWS);

/**
 * Does this `?view=` belong to the store?
 *
 * Its own question, like `isAccountingView`, and for the same reason: `dashboard`
 * is a word the accounting hub also uses, so a single map keyed on the bare view
 * name would have to pick a winner. Asked only by a caller that has already
 * decided it is looking at `/store`.
 */
export const isStoreView = (view) => !!view && STORE_SET.has(view);

const PAGE_OF = new Map([
  ...EXPENSES_VIEWS.map((v) => [v, EXPENSES_PATH]),
  ...GOODS_VIEWS.map((v) => [v, GOODS_PATH]),
]);

/**
 * The page a `?view=` lives on, or `null` for a key neither page declares.
 *
 * `null` is the answer for a typo, a retired key, or the skeleton-only alias
 * `setup` — and every caller treats it the same way: leave the URL alone and
 * let the page normalise it to the first view that person may actually open.
 * Forwarding an unknown view somewhere would turn a harmless bad bookmark into
 * a trip through a page the reader never asked for.
 */
export function pageForView(view) {
  return (view && PAGE_OF.get(view)) || null;
}

/** Did this view move to «کالاها و خدمات»? The router's forwarding question. */
export const isGoodsView = (view) => pageForView(view) === GOODS_PATH;
